/**
 * Plain sentences for TikTok's documented error codes and `fail_reason`
 * values, so a marketer reads what to do instead of an API token.
 *
 * Sources (read 2026-11-03):
 * - error codes: https://developers.tiktok.com/doc/content-posting-api-reference-direct-post
 *   and https://developers.tiktok.com/doc/content-posting-api-reference-photo-post
 * - status and fail_reason: https://developers.tiktok.com/doc/content-posting-api-reference-get-video-status
 */

const ERROR_SENTENCES: Record<string, string> = {
  invalid_param: 'TikTok did not accept part of this post.',
  app_version_check_failed:
    'Sending photos to TikTok drafts needs TikTok app version 31.8 or later. Update the TikTok app and try again.',
  spam_risk_too_many_posts:
    'This TikTok account has reached its daily limit for posts made through apps. Post from the TikTok app, or try again tomorrow.',
  spam_risk_user_banned_from_posting:
    'TikTok has stopped this account from posting, so Mora cannot send this post.',
  spam_risk_too_many_pending_share:
    'This TikTok account already has 5 drafts waiting. Finish or clear them in TikTok, then try again.',
  reached_active_user_cap:
    "TikTok's daily limit on accounts posting through Mora has been reached. Try again tomorrow.",
  unaudited_client_can_only_post_to_private_accounts:
    "Mora's TikTok connection can only post privately until TikTok approves it.",
  url_ownership_unverified:
    "TikTok has not verified the address Mora loads photos from, so it cannot fetch them. This needs a one-time setup in Mora's TikTok developer account.",
  privacy_level_option_mismatch:
    'That audience is not available for this TikTok account. Choose another one.',
  access_token_invalid: 'The TikTok connection has expired. Reconnect the account and try again.',
  scope_not_authorized:
    'Mora does not have permission to post for this TikTok account. Reconnect the account and allow posting.',
  rate_limit_exceeded: 'TikTok is limiting requests right now. Try again in a minute.',
  internal_error: 'TikTok had a temporary problem. Try again in a few minutes.',
  invalid_publish_id: 'TikTok could not find this upload. Try posting again.',
  token_not_authorized_for_specified_publish_id:
    'TikTok could not find this upload. Try posting again.',
};

const FAIL_REASON_SENTENCES: Record<string, string> = {
  file_format_check_failed:
    'TikTok could not read this file. Use MP4, MOV or WebM for video, and JPEG for photos.',
  duration_check_failed:
    "TikTok rejected the video's length. Check it is within this account's limit.",
  frame_rate_check_failed:
    "TikTok does not accept this video's frame rate. Use between 23 and 60 frames per second.",
  picture_size_check_failed:
    'TikTok does not accept this picture size. Photos can be up to 1080 pixels on the long side.',
  internal: 'TikTok had a temporary problem. Try again in a few minutes.',
  video_pull_failed: 'TikTok could not download the video. Try again.',
  photo_pull_failed: 'TikTok could not download the photos. Try again.',
  publish_cancelled: 'The post was cancelled before it finished.',
  auth_removed:
    "This TikTok account removed Mora's access while the post was processing. Reconnect the account to post.",
  spam_risk_too_many_posts:
    'This TikTok account has reached its daily limit for posts made through apps. Post from the TikTok app, or try again tomorrow.',
  spam_risk_user_banned_from_posting:
    'TikTok has stopped this account from posting, so Mora cannot send this post.',
  spam_risk_text: 'TikTok flagged the caption as spam. Change the caption and try again.',
  spam_risk: 'TikTok flagged this post as risky and did not publish it.',
};

/** fail_reason values that are worth an automatic or manual retry. */
const RETRYABLE_FAIL_REASONS = new Set(['internal', 'video_pull_failed', 'photo_pull_failed']);

export function isRetryableTikTokFailReason(reason: unknown): boolean {
  return typeof reason === 'string' && RETRYABLE_FAIL_REASONS.has(reason);
}

/**
 * A plain sentence for a TikTok error code, or `null` when the code is not one
 * TikTok documents. `invalid_param` carries TikTok's own detail after the
 * sentence because that text names the field that was wrong.
 */
export function tiktokErrorSentence(code: unknown, providerMessage?: unknown): string | null {
  if (typeof code !== 'string') return null;
  const sentence = ERROR_SENTENCES[code];
  if (!sentence) return null;
  if (code === 'invalid_param' && typeof providerMessage === 'string' && providerMessage.trim()) {
    return `${sentence} ${providerMessage.trim()}`;
  }
  return sentence;
}

/** A plain sentence for a status `fail_reason`. Unknown reasons still read as a
 * sentence rather than a raw token. */
export function tiktokFailReasonSentence(reason: unknown): string {
  if (typeof reason === 'string' && FAIL_REASON_SENTENCES[reason]) {
    return FAIL_REASON_SENTENCES[reason];
  }
  return 'TikTok could not process this post. Try again, or post from the TikTok app.';
}

/** Whether a message from `post-to-tiktok` already is one of these sentences. */
export function isTikTokPlainSentence(message: string): boolean {
  return (
    Object.values(ERROR_SENTENCES).some((sentence) => message.startsWith(sentence)) ||
    Object.values(FAIL_REASON_SENTENCES).includes(message)
  );
}

/** Every sentence above, so the shared failure mapper can pass them through untouched. */
export function tiktokPlainSentences(): Array<{ code: string; message: string }> {
  return [
    ...Object.entries(ERROR_SENTENCES).map(([code, message]) => ({ code: `tt_${code}`, message })),
    ...Object.entries(FAIL_REASON_SENTENCES).map(([code, message]) => ({
      code: `tt_fail_${code}`,
      message,
    })),
  ];
}
